import { useEffect, useMemo, useState } from 'react';
import type { MediaSignal, MediaSignalBulletin } from '@tsr/shared-types';
import { useElementSize } from './lib/useElementSize';
import { useWallQueueItems } from './wallQueueData';
import {
  TICKER_BULLETIN_WINDOW_H,
  TICKER_HEADLINE_WINDOW_H,
  selectTakeoverItems,
  selectTickerItems,
  tickerDurationS,
  type TickerItem,
  type TickerTakeoverItem,
} from './wallTicker';

/**
 * WALL bottom-ticker zone (#263) — the wall-only sliding news crawl with the
 * URGENT-RED takeover. Content model + selection rules live in `wallTicker.ts`
 * (pure); this zone fetches on the wall cadence and renders the crawl.
 *
 * Motion posture: this zone joins the media tiles as a designated continuous-
 * motion zone (PRD §4.26.2 wall composition) — the crawl slides in BOTH modes.
 * The map's motion law (URGENT-RED pulse + live-green breathe only) governs map
 * markers and is untouched. Honest states: a quiet wire renders a static
 * "wire quiet" line — never fake motion over no content.
 *
 * READ-ONLY like every wall zone: acknowledging the urgent that took the
 * ticker over happens on the COP / panel queue; the takeover clears itself on
 * the next refresh after every urgent is acked.
 */

/** Wall cadence — refresh content + urgency every 30s (the wall standard). */
const TICKER_REFRESH_MS = 30_000;

interface AmbientWire {
  bulletins: MediaSignalBulletin[];
  signals: MediaSignal[];
}

export function WallTickerZone(): JSX.Element {
  // Urgency = the SHARED wall queue sweep (`useWallQueueItems`) — the same item
  // model the hero's count chips read, so "unacked urgent" can never disagree
  // between the chip strip and the takeover.
  const queue = useWallQueueItems();
  const [wire, setWire] = useState<AmbientWire>({ bulletins: [], signals: [] });

  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();

    const refresh = async (): Promise<void> => {
      try {
        const bulletinQs = `hours=${TICKER_BULLETIN_WINDOW_H}&limit=20`;
        const signalQs = `hours=${TICKER_HEADLINE_WINDOW_H}&limit=200&source=gdelt,rss`;
        const [bulletinRes, signalRes] = await Promise.all([
          fetch(`/api/media-signal-bulletins?${bulletinQs}`, { signal: ctl.signal }),
          fetch(`/api/media-signals?${signalQs}`, { signal: ctl.signal }),
        ]);
        if (cancelled) return;

        const bulletins: MediaSignalBulletin[] = bulletinRes.ok
          ? ((await bulletinRes.json()) as { bulletins?: MediaSignalBulletin[] }).bulletins ?? []
          : [];
        const signals: MediaSignal[] = signalRes.ok
          ? ((await signalRes.json()) as { signals?: MediaSignal[] }).signals ?? []
          : [];
        if (cancelled) return;
        setWire({ bulletins, signals });
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          console.error('WallTickerZone refresh failed', err);
        }
      }
    };

    void refresh();
    const timer = setInterval(() => void refresh(), TICKER_REFRESH_MS);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(timer);
    };
  }, []);

  const nowMs = Date.now();
  const items = useMemo(
    () => selectTickerItems({ bulletins: wire.bulletins, signals: wire.signals, nowMs }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nowMs ticks every render; wire drives recompute
    [wire],
  );
  const takeovers = useMemo(
    () => selectTakeoverItems(queue, nowMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nowMs ticks every render; queue drives recompute
    [queue],
  );

  return <WallTicker items={items} takeovers={takeovers} />;
}

/**
 * Presentational ticker band (exported for tests): URGENT-RED takeover when any
 * unacked urgent exists, else the ambient crawl, else an honest static quiet
 * line. The crawl duplicates its track and translates −50% on a linear loop
 * (`wall-ticker-scroll` keyframes); duration scales with measured track width
 * so the speed stays constant regardless of content length.
 */
export function WallTicker({
  items,
  takeovers,
}: {
  items: TickerItem[];
  takeovers: TickerTakeoverItem[];
}): JSX.Element {
  const takeover = takeovers.length > 0;
  return (
    <section
      data-testid="wall-ticker"
      data-mode={takeover ? 'takeover' : 'ambient'}
      className={`flex h-full w-full items-stretch overflow-hidden ${
        takeover ? 'bg-[#3a0a08]' : 'bg-[#0b1020]'
      }`}
    >
      {/* Mode chip — anchors the band's identity at wall distance. */}
      <div
        className={`flex shrink-0 items-center gap-2 px-4 text-sm font-bold uppercase tracking-widest ${
          takeover ? 'bg-[#ff3b30] text-white' : 'border-r border-slate-800 text-slate-400'
        }`}
        data-testid="wall-ticker-chip"
      >
        {takeover ? (
          <>
            <span aria-hidden>⚠</span>
            <span>
              Urgent{takeovers.length > 1 ? ` ×${takeovers.length}` : ''} · unacknowledged
            </span>
          </>
        ) : (
          <span>Wire</span>
        )}
      </div>

      {takeover ? (
        <TickerCrawl
          key={`takeover:${takeovers.map((t) => t.id).join('|')}`}
          trackTestId="wall-ticker-takeover-track"
        >
          {takeovers.map((t) => (
            <span key={t.id} data-testid="wall-ticker-urgent" className="mx-8 inline-flex items-baseline gap-3">
              <span className="text-base font-bold text-[#ff6b61]">{t.headline}</span>
              <span className="text-sm text-red-100/90">{t.detail}</span>
              <span className="text-sm font-semibold uppercase tracking-wider text-[#ff6b61]">
                · acknowledge on the COP ·
              </span>
            </span>
          ))}
        </TickerCrawl>
      ) : items.length === 0 ? (
        // Honest quiet state — no content, no fake motion.
        <div
          data-testid="wall-ticker-quiet"
          className="flex min-w-0 flex-1 items-center px-4 text-sm text-slate-500"
        >
          Wire quiet — no fresh bulletins or headlines.
        </div>
      ) : (
        <TickerCrawl
          key={`ambient:${items.map((i) => i.id).join('|')}`}
          trackTestId="wall-ticker-ambient-track"
        >
          {items.map((it) => (
            <span key={it.id} data-testid={`wall-ticker-item-${it.kind}`} className="mx-8 inline-flex items-baseline gap-3">
              <span
                className={`text-xs font-semibold uppercase tracking-wider ${
                  it.kind === 'bulletin' ? 'text-amber-300/90' : 'text-sky-300/80'
                }`}
              >
                {it.kicker}
              </span>
              <span className="text-base text-slate-200">{it.text}</span>
            </span>
          ))}
        </TickerCrawl>
      )}
    </section>
  );
}

/** The sliding loop: content rendered TWICE so a −50% translate loops
 *  seamlessly; duration derives from the measured (duplicated) track width. */
function TickerCrawl({
  children,
  trackTestId,
}: {
  children: React.ReactNode;
  trackTestId: string;
}): JSX.Element {
  const [trackRef, trackSize] = useElementSize<HTMLDivElement>();
  const durationS = useMemo(() => tickerDurationS(trackSize.width), [trackSize.width]);
  return (
    <div className="relative min-w-0 flex-1 overflow-hidden">
      <div
        ref={trackRef}
        data-testid={trackTestId}
        className="wall-ticker-track absolute inset-y-0 flex w-max items-center whitespace-nowrap"
        style={{ animationDuration: `${durationS}s` }}
      >
        <div className="flex items-center">{children}</div>
        {/* The seamless-loop duplicate (decorative for a11y). */}
        <div className="flex items-center" aria-hidden>
          {children}
        </div>
      </div>
    </div>
  );
}
